import { useState, FormEvent } from 'react';
import { Modal, Input, Button, Alert } from '@/shared/components/ui';
import { useAuthStore } from '../../store/useAuthStore';
import { useLock } from '../../model/useLock';
import { useAuth } from '../../model/useAuth';
import { authService } from '../../api/authService';
import { MENSAJES_ERROR, TEXTOS_UI } from '../../constants';
import { useTheme } from '@/app/hooks/useTheme';
import { Moon, Sun } from 'lucide-react';
import styles from './PantallaBloqueo.module.css';

/**
 * PantallaBloqueo - Modal fullscreen de bloqueo de aplicación
 * 
 * Bounded Context: Autenticación
 * Responsabilidad: Interfaz de desbloqueo con validación de contraseña
 * 
 * Características:
 * - Validación contraseña con backend
 * - Opción logout alternativa (FIX: usa logout() correcto del hook)
 * - No se cierra con Escape o click fuera
 * - Autofocus en input
 * - Enter key submit
 */
export const PantallaBloqueo = () => {
    const { user } = useAuthStore();
    const { isLocked, unlock } = useLock();
    const { logout } = useAuth();  // FIX: logout es el retorno correcto

    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { theme, toggleTheme } = useTheme();

    const isDarkMode = theme === 'dark' || theme === 'sapphire';

    // Theme toggle button para rightAddon (patrón LoginForm)
    const themeToggleButton = (
        <button
            type="button"
            onClick={toggleTheme}
            className={styles.themeToggle}
            title={isDarkMode ? 'Modo claro' : 'Modo oscuro'}
            aria-label="Cambiar tema"
        >
            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
        </button>
    );

    const handleUnlock = async (e: FormEvent) => {
        e.preventDefault();
        setError('');

        if (!password.trim()) {
            setError(MENSAJES_ERROR.CONTRASENA_REQUERIDA || 'Por favor ingresa tu contraseña');
            return;
        }

        try {
            setLoading(true);

            // Validar contraseña con backend - usar userId (string) no id (bigint)
            const isValid = await authService.validatePassword({
                usuarioId: user!.userId || String(user!.id), // Usar userId (string) si existe, sino convertir id
                contrasena: password,
            });

            if (isValid) {
                unlock();
                setPassword('');
            } else {
                setError(MENSAJES_ERROR.CONTRASENA_INCORRECTA || 'Contraseña incorrecta');
            }
        } catch (err: any) {
            setError(err.message || MENSAJES_ERROR.ERROR_VALIDAR_CONTRASENA || 'Error al validar contraseña');
        } finally {
            setLoading(false);
        }
    };

    // FIX: Logout correcto usando logout del hook auth
    const handleLogoutClick = async () => {
        unlock(); // Desbloquear primero
        await logout(); // Logout real que navega a /login
    };

    if (!isLocked || !user) return null;

    return (
        <Modal
            isOpen={isLocked}
            onClose={() => { }} // No cerrable desde fuera
            title="Aplicación Bloqueada"
            size="md"
            closeOnOverlayClick={false}
        >
            <div className={styles.lockContent}>
                {/* Avatar y info usuario */}
                <div className={styles.userInfo}>
                    <div className={styles.avatar}>
                        {user.name.charAt(0).toUpperCase()}
                    </div>
                    <h2>{user.name}</h2>
                    <p className={styles.description}>
                        {TEXTOS_UI.APP_BLOQUEADA || 'La aplicación está bloqueada'}
                    </p>
                </div>

                {/* Formulario desbloqueo */}
                <form onSubmit={handleUnlock} className={styles.form}>
                    <Input
                        type="password"
                        label="Contraseña"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Ingresa tu contraseña"
                        showPasswordToggle
                        autoFocus
                        disabled={loading}
                        autoComplete="new-password"
                        rightAddon={themeToggleButton}
                    />

                    {error && (
                        <Alert type="error">{error}</Alert>
                    )}

                    <div className={styles.actions}>
                        <Button
                            variant="primary"
                            type="submit"
                            loading={loading}
                            disabled={loading || !password.trim()}
                        >
                            {TEXTOS_UI.DESBLOQUEAR || 'Desbloquear'}
                        </Button>

                        <Button
                            variant="secondary"
                            type="button"
                            onClick={handleLogoutClick}
                            disabled={loading}
                        >
                            {TEXTOS_UI.CERRAR_SESION || 'Cerrar Sesión'}
                        </Button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};
